import { AnimatePresence, motion } from "motion/react";
import { ArrowRight, Check, ChevronDown, Lightbulb, LoaderCircle, LockKeyhole, RotateCcw, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, type ProductIdea, type ProductIdeasJob, type Session } from "../../app/api";
import { getTelegramWebApp } from "../../app/telegram";
import { Button } from "../../components/ui/Button";
import "./product-ideas.css";

type Step = "checking" | "form" | "membership" | "waiting" | "result" | "error" | "used";
const CHANNEL_URL = "https://t.me/ai_helper_vakula";
const INPUT_ERROR = "Расскажите чуть конкретнее, чем вы увлекаетесь или что умеете делать. Например: «люблю машины и хорошо разбираюсь в подборе б/у авто».";
const activeStatuses = new Set(["queued", "submitting", "running"]);
const transition = { duration: .24, ease: [0.2, 0, 0, 1] as const };

function failureMessage(code?: string | null) {
  const messages: Record<string, string> = {
    INPUT_NOT_SPECIFIC: INPUT_ERROR,
    PROVIDER_OUTCOME_UNKNOWN: "Связь с нейросетью прервалась. Эта попытка отправлена на проверку.",
    PROVIDER_RESPONSE_INVALID: "Нейросеть вернула неполный ответ. Попробуй ещё раз.",
    OPENAI_HTTP_429: "Сейчас слишком много запросов. Попробуй ещё раз немного позже.",
    BUDGET_LIMIT: "Лимит генераций на сегодня исчерпан.",
    QUEUE_FULL: "Сейчас много запросов. Попробуй чуть позже.",
    GENERATION_PAUSED: "Генерации временно приостановлены.",
    MEMBERSHIP_UNKNOWN: "Не удалось проверить подписку. Попробуй ещё раз.",
    SUBSCRIPTION_REQUIRED: "Сначала подпишись на канал.",
    PRODUCT_IDEAS_LIMIT: "Три попытки на этом аккаунте уже использованы.",
    FEATURE_OWNER_ONLY: "Эта фишка пока недоступна.",
    NETWORK_ERROR: "Соединение прервалось. Повтори запрос — новая попытка не создастся.",
    GENERATION_TIMEOUT: "Ответ занимает больше обычного. Он сохранится здесь автоматически.",
  };
  return messages[code ?? ""] ?? "Не получилось собрать идеи. Попробуй ещё раз.";
}

function Status({ icon, title, message, spinning, children }: { icon: React.ReactNode; title: string; message: string; spinning?: boolean; children?: React.ReactNode }) {
  return <div className="ideas-status"><div className={spinning ? "ideas-status-icon spinning" : "ideas-status-icon"}>{icon}<i/></div><h2>{title}</h2><p>{message}</p>{children}</div>;
}

function IdeaCard({ idea, index }: { idea: ProductIdea; index: number }) {
  return <details className="idea-result-card" open={index === 0}>
    <summary><span>{String(index + 1).padStart(2, "0")}</span><strong>{idea.title}</strong><ChevronDown/></summary>
    <div className="idea-result-body">
      <section><small>Что это</small><p>{idea.what_it_is}</p></section>
      <section><small>Для кого</small><p>{idea.audience}</p></section>
      <section><small>Какую проблему решает</small><p>{idea.problem}</p></section>
      <section><small>Что получает покупатель</small><p>{idea.buyer_gets}</p></section>
      <section><small>Как сделать</small><p>{idea.how_to_make}</p></section>
      <div className="idea-result-facts"><section><small>Сколько делать</small><strong>{idea.build_time}</strong></section><section><small>Сколько можно брать</small><strong>{idea.price}</strong></section></div>
      <section className="idea-content-examples"><small>Как продавать через соцсети</small><ol>{idea.social_content.map((item) => <li key={item}>{item}</li>)}</ol></section>
    </div>
  </details>;
}

export function ProductIdeasTool({ session, onClose }: { session?: Session; onClose(): void }) {
  const [step, setStep] = useState<Step>("checking");
  const [text, setText] = useState("");
  const [cards, setCards] = useState<ProductIdea[]>([]);
  const [message, setMessage] = useState("");
  const [membership, setMembership] = useState<"checking" | "not_member" | "unknown">("checking");
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [remaining, setRemaining] = useState(3);
  const [recoverJob, setRecoverJob] = useState(false);
  const pendingKey = useRef("");
  const sheet = useRef<HTMLElement>(null);
  const closed = useRef(false);

  useEffect(() => () => { closed.current = true; }, []);
  useEffect(() => { sheet.current?.scrollTo?.({ top: 0 }); }, [step]);
  useEffect(() => {
    const back = getTelegramWebApp()?.BackButton;
    if (!back) return;
    back.show(); back.onClick(onClose);
    return () => { back.offClick(onClose); back.hide(); };
  }, [onClose]);

  const showJob = useCallback((job: ProductIdeasJob) => {
    setRecoverJob(false);
    if (job.status === "succeeded" && job.result?.cards.length) {
      setCards(job.result.cards); setStep("result"); return true;
    }
    if (job.status === "invalid_input") {
      setMessage(INPUT_ERROR); setStep("error"); return true;
    }
    if (["failed", "needs_reconciliation"].includes(job.status)) {
      setMessage(failureMessage(job.error_code)); setStep("error"); return true;
    }
    return false;
  }, []);

  const poll = useCallback(async (id: string, token: string) => {
    let networkErrors = 0;
    for (let count = 0; count < 120 && !closed.current; count += 1) {
      if (document.hidden) {
        await new Promise<void>((resolve) => document.addEventListener("visibilitychange", () => resolve(), { once: true }));
        if (closed.current) return;
      }
      try {
        const { job } = await api.productIdeasJob(id, token);
        networkErrors = 0;
        if (!activeStatuses.has(job.status)) { showJob(job); return; }
      } catch (error) {
        networkErrors += 1;
        if (networkErrors >= 15) throw error;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 2000));
    }
    if (!closed.current) throw new Error("GENERATION_TIMEOUT");
  }, [showJob]);

  const restore = useCallback(async () => {
    if (!session) { setMessage("Закрой и снова открой приложение из бота."); setStep("error"); return; }
    setStep("checking");
    try {
      const [access, history] = await Promise.all([api.productIdeasAccess(session.access_token), api.productIdeasJobs(session.access_token)]);
      if (closed.current) return;
      setAttemptsUsed(access.attempts_used); setRemaining(access.remaining);
      const latest = history.items[0];
      if (latest && activeStatuses.has(latest.status)) {
        setStep("waiting"); await poll(latest.id, session.access_token); return;
      }
      if (latest && showJob(latest)) return;
      setStep(access.remaining > 0 ? "form" : "used");
    } catch (error) {
      setRecoverJob(true);
      setMessage(failureMessage(error instanceof Error ? error.message : "")); setStep("error");
    }
  }, [poll, session, showJob]);

  useEffect(() => { void restore(); }, [restore]);

  async function start() {
    const value = text.trim();
    if (!session || !value || value.length > 200) return;
    let accepted = false;
    try {
      setMessage(""); setRecoverJob(false);
      if (session.user.role !== "owner") {
        setMembership("checking"); setStep("membership");
        const checked = await api.membership(session.access_token);
        if (checked.state !== "member") { setMembership(checked.state === "not_member" ? "not_member" : "unknown"); return; }
      }
      setStep("waiting");
      if (!pendingKey.current) pendingKey.current = crypto.randomUUID();
      const { job } = await api.createProductIdeas(value, pendingKey.current, session.access_token);
      accepted = true; pendingKey.current = "";
      setAttemptsUsed((current) => current + 1); setRemaining((current) => Math.max(0, current - 1));
      await poll(job.id, session.access_token);
    } catch (error) {
      const code = error instanceof Error ? error.message : "";
      if (accepted) pendingKey.current = "";
      if (code === "PRODUCT_IDEAS_LIMIT") { setRemaining(0); setStep("used"); return; }
      setRecoverJob(accepted);
      setMessage(failureMessage(code)); setStep("error");
    }
  }

  function another() { setText(""); setCards([]); setMessage(""); setRecoverJob(false); pendingKey.current = ""; setStep(remaining > 0 ? "form" : "used"); }
  function openChannel(event: React.MouseEvent<HTMLAnchorElement>) { const telegram = getTelegramWebApp(); if (telegram?.openTelegramLink) { event.preventDefault(); telegram.openTelegramLink(CHANNEL_URL); } }

  return <motion.div className="sheet-backdrop ideas-backdrop" role="dialog" aria-modal="true" aria-label="Идеи цифровых продуктов" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: .16 }}>
    <motion.section ref={sheet} className="sheet ideas-sheet" initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} transition={transition}>
      <span className="sheet-handle"/><button className="icon-button" onClick={onClose} aria-label="Закрыть"><X/></button>
      <AnimatePresence mode="wait"><motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={transition}>
        {step === "checking" && <Status spinning icon={<LoaderCircle/>} title="Проверяем попытки" message="Секунду…"/>}
        {step === "form" && <div className="ideas-form"><span className="overline">Идеи под твои навыки</span><h2>Что тебе нравится<br/>и что ты умеешь?</h2><p>Напиши одним сообщением. Даже одного увлечения или навыка достаточно.</p><label><textarea autoFocus maxLength={200} rows={5} value={text} onChange={(event) => { setText(event.target.value); if (pendingKey.current) pendingKey.current = ""; }} placeholder="Например: люблю машины, умею подбирать хорошие б/у авто"/><span>{text.length} / 200</span></label><div className="ideas-attempts"><Lightbulb/><span>Осталось попыток: <strong>{remaining} из 3</strong></span></div><Button disabled={!text.trim()} onClick={() => void start()}>Получить идеи <ArrowRight/></Button></div>}
        {step === "membership" && membership === "checking" && <Status spinning icon={<LoaderCircle/>} title="Проверяем подписку" message="Обычно это занимает пару секунд."/>}
        {step === "membership" && membership === "not_member" && <Status icon={<LockKeyhole/>} title="Сначала подпишись" message="Генератор идей доступен подписчикам канала."><a className="button channel-button" href={CHANNEL_URL} onClick={openChannel}>Подписаться на канал <ArrowRight/></a><button className="secondary-action membership-retry" onClick={() => void start()}>Я подписался — проверить</button></Status>}
        {step === "membership" && membership === "unknown" && <Status icon={<LoaderCircle/>} title="Не смогли проверить" message="Telegram не ответил. Попробуем ещё раз."><Button onClick={() => void start()}>Проверить подписку <ArrowRight/></Button></Status>}
        {step === "waiting" && <div className="ideas-waiting"><div className="ideas-thinking"><Lightbulb/><i/><b/></div><span className="overline">Собираем продукты</span><h2>Ищем сильные идеи</h2><p>Сначала перебираем варианты, потом оставляем только те, которые реально можно быстро собрать и продавать.</p><div className="ideas-thinking-lines"><i/><i/><i/></div><small>Можно закрыть — результат сохранится</small></div>}
        {step === "result" && <div className="ideas-results"><header><span className="overline">Готовые решения</span><h2>{cards.length === 5 ? "5 идей для тебя" : `${cards.length} ${cards.length === 1 ? "идея" : "идеи"} для тебя`}</h2><p>Открой карточку — внутри продукт, покупатель, цена и конкретный контент для продажи.</p></header><div className="idea-result-list">{cards.map((card, index) => <IdeaCard key={`${index}-${card.title}`} idea={card} index={index}/>)}</div><div className="ideas-result-footer"><span><Check/> Попытка {attemptsUsed} из 3</span>{remaining > 0 ? <Button onClick={another}>Получить другие идеи <RotateCcw/></Button> : <Button onClick={onClose}>Готово <Check/></Button>}</div></div>}
        {step === "error" && <Status icon={<X/>} title={message === INPUT_ERROR ? "Нужно чуть конкретнее" : "Не получилось"} message={message}>{recoverJob ? <Button onClick={() => void restore()}>Проверить результат <ArrowRight/></Button> : remaining > 0 ? <Button onClick={() => pendingKey.current ? void start() : another()}>Попробовать ещё раз <ArrowRight/></Button> : <Button onClick={() => setStep("used")}>Понятно</Button>}</Status>}
        {step === "used" && <Status icon={<LockKeyhole/>} title="Попытки закончились" message="Для одного Telegram-аккаунта доступно три генерации идей."><Button onClick={onClose}>Понятно</Button></Status>}
      </motion.div></AnimatePresence>
    </motion.section>
  </motion.div>;
}
